import React, { useId } from 'react';
import { t } from 'i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { navigateSafely } from '../../utils/navigation';
import { ToastIntent, Toaster, makeStyles, useToastController } from '@fluentui/react-components';
import SpinnerComponent from '../../common/components/controls/spinner';
import { getToastMessageComponent } from '../../common/components/ToastMessage';
import { getEnvironment360URL } from '../../services/configs/appConfig';
import { deleteTemporaryImportArchiveDocument, saveMultipleEmails } from '../../services/api/dataServiceOutlook';

const useStyles = makeStyles({
    spinnerStyles: {
        paddingTop: '30vh'
    },
});

const normalizeRecnoValue = (value: unknown): string | null => {
    if (typeof value === 'string') {
        const normalizedValue = value.trim();
        return normalizedValue.length > 0 ? normalizedValue : null;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value);
    }

    return null;
};

const normalizeRecno = (recno: unknown): string | null => {
    const directRecno = normalizeRecnoValue(recno);
    if (directRecno) {
        return directRecno;
    }

    if (!recno || typeof recno !== 'object') {
        return null;
    }

    const recnoObject = recno as Record<string, unknown>;
    return (
        normalizeRecnoValue(recnoObject.recno) ??
        normalizeRecnoValue(recnoObject.Recno) ??
        normalizeRecnoValue(recnoObject.DocumentRecno) ??
        normalizeRecnoValue(recnoObject.documentRecno)
    );
};

const normalizeItemId = (itemId: unknown): string | null => {
    if (typeof itemId !== 'string') {
        return null;
    }

    const normalizedItemId = itemId.trim();
    return normalizedItemId.length > 0 ? normalizedItemId : null;
};

const hasStringValue = (value: string | null): value is string => value !== null;

type ArchiveMultipleMailLocationState = {
    itemIds?: string[];
};

const ArchiveMultipleMail: React.FC = () => {
    const loadingText = t('Saving selected emails to 360...');
    const [showSpinner, setShowSpinner] = React.useState(false);
    const [hasArchiveStarted, setHasArchiveStarted] = React.useState(false);
    const toasterId = useId();
    const { dispatchToast, dismissToast } = useToastController(toasterId);
    const navigate = useNavigate();
    const location = useLocation();
    const archiveRecnosRef = React.useRef<string[]>([]);
    const cleanupStartedRef = React.useRef(false);
    const cleanupPromiseRef = React.useRef<Promise<void> | null>(null);

    const cleanupTemporaryDocuments = React.useCallback((): Promise<void> => {
        if (cleanupStartedRef.current) {
            return cleanupPromiseRef.current ?? Promise.resolve();
        }

        const documentRecnos = Array.from(new Set(archiveRecnosRef.current)).join(',');
        if (!documentRecnos) {
            return Promise.resolve();
        }

        cleanupStartedRef.current = true;
        cleanupPromiseRef.current = deleteTemporaryImportArchiveDocument(documentRecnos)
            .then(() => undefined, () => undefined);
        return cleanupPromiseRef.current;
    }, []);

    React.useEffect(() => {

        const onMessage = (event: MessageEvent<unknown>) => {
            const expectedOrigin = new URL(getEnvironment360URL()).origin;
            if (event.origin !== expectedOrigin || (event.data !== 'Cancel' && event.data !== 'Ok')) return;

            if (event.data === 'Ok' || cleanupStartedRef.current) { // No need to cleanup if the save successfully or cleanup has already started
                return;
            }

            void cleanupTemporaryDocuments();
        };

        globalThis.addEventListener('message', onMessage);
        return () => globalThis.removeEventListener('message', onMessage);
    }, [cleanupTemporaryDocuments]);

    const itemIdsToArchive = React.useMemo(() => {
        const state = location.state as ArchiveMultipleMailLocationState | null;
        // Deduplicate itemIds to ensure no duplicates are sent to API
        const itemIds = state?.itemIds ?? [];
        return Array.from(new Set(itemIds));
    }, [location.state]);

    const onDialogClosed = React.useCallback(async () => {
        await cleanupPromiseRef.current;
        setShowSpinner(false);
        navigateSafely(navigate, '/archivehome?multiSaved=true');
    }, [navigate]);

    const showToast = React.useCallback((message: string, toastType: ToastIntent) => {
        dispatchToast(
            getToastMessageComponent(message, () => navigateSafely(navigate, '/archiveMultipleMail')),
            { timeout: -1, intent: toastType }
        );
    }, [dispatchToast, navigate]);

    const openOfficeDialogToArchive = React.useCallback((recnos: string[]) => {
        archiveRecnosRef.current = recnos;
        cleanupStartedRef.current = false;
        cleanupPromiseRef.current = null;
        const recnoParam = encodeURIComponent(JSON.stringify(recnos));
        Office.context.ui.displayDialogAsync(
            `${globalThis.location.origin}/multiplemaildialogredirect.html?recnos=${recnoParam}&public360Url=${encodeURIComponent(getEnvironment360URL())}`,
            { height: 70, width: 52, promptBeforeOpen: false },
            (asyncResult) => {
                if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                    setShowSpinner(false);
                    const errorMessage = `${String(t('Failed to open multi-email dialog: '))}${String(asyncResult.error.message)}`;
                    showToast(errorMessage, 'error');
                } else {
                    const dialog = asyncResult.value;
                    dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
                        void onDialogClosed();
                    });
                }
            }
        );
    }, [onDialogClosed, showToast]);

    const archiveSelectedEmails = React.useCallback(() => {
        // Prevent duplicate archive calls
        if (hasArchiveStarted) {
            return;
        }
        setHasArchiveStarted(true);
        dismissToast(toasterId);
        setShowSpinner(true);

        void saveMultipleEmails((results) => {
            const successfulResults = results
                .filter(r => r.IsSuccess)
                .map(r => ({
                    recno: normalizeRecno(r.Recno),
                    itemId: normalizeItemId(r.ItemId)
                }))
                .filter((result): result is { recno: string; itemId: string | null } => Boolean(result.recno));

            const successfulRecnos = successfulResults
                .map(result => result.recno)
                .filter(hasStringValue);
            const failedEmails = results.filter(r => !r.IsSuccess);

            if (failedEmails.length > 0) {
                const failureMsg = failedEmails.map(f => `${f.Subject}: ${f.ErrorMessage}`).join(", ");
                showToast(`${String(t('Some emails failed to save: '))}${failureMsg}`, 'error');
            }

            if (successfulRecnos.length === 0) {
                setShowSpinner(false);
                showToast(t('No emails were saved'), 'error');
                return;
            }
            openOfficeDialogToArchive(successfulRecnos);
        }, (err: string) => {
            setShowSpinner(false);
            showToast(err, 'error');
        }, itemIdsToArchive);
    }, [dismissToast, itemIdsToArchive, openOfficeDialogToArchive, showToast, toasterId, hasArchiveStarted]);

    React.useEffect(() => {
        archiveSelectedEmails();
    }, [archiveSelectedEmails]);

    return (
        <>
            <Toaster toasterId={toasterId} />
            <SpinnerComponent showSpinner={showSpinner} size='medium' label={loadingText} labelPosition='after' spinnerStyle={useStyles().spinnerStyles} />
        </>
    );
};

export default ArchiveMultipleMail;
